/**
 * contact-import-lib.ts — phần thuần của trang "Nhập khách hàng":
 * đọc CSV/XLSX, đoán cột theo tiêu đề, dựng dòng gửi API, xuất CSV lỗi / file mẫu.
 *
 * XLSX: không thêm thư viện — đọc thẳng file ZIP bằng `DecompressionStream`
 * (có sẵn trong trình duyệt hiện đại và Node ≥ 18) và bóc XML bằng regex. Chỉ lấy
 * sheet ĐẦU TIÊN, giá trị đã tính (không tính lại công thức).
 */
import { i18n } from '@/lib/i18n'

export const MAX_IMPORT_ROWS = 10_000

/** Trường đích của một cột. `prop:<fieldKey>` = thuộc tính CDP. '' = bỏ qua cột. */
export type BuiltinField = 'fullName' | 'phone' | 'email' | 'tags' | 'lifecycleStage' | 'source' | 'notes'
export type TargetField = BuiltinField | `prop:${string}` | ''

export const BUILTIN_FIELDS: { key: BuiltinField; label: string }[] = [
  { key: 'fullName', get label() { return i18n.t('custList:import.fields.fullName') } },
  { key: 'phone', get label() { return i18n.t('custList:import.fields.phone') } },
  { key: 'email', get label() { return i18n.t('custList:import.fields.email') } },
  { key: 'tags', get label() { return i18n.t('custList:import.fields.tags') } },
  { key: 'lifecycleStage', get label() { return i18n.t('custList:import.fields.lifecycleStage') } },
  { key: 'source', get label() { return i18n.t('custList:import.fields.source') } },
  { key: 'notes', get label() { return i18n.t('custList:import.fields.notes') } },
]

/** Tiêu đề cột của file mẫu — dữ liệu định dạng, luôn là tiếng Việt để nhập lại khớp cột. */
const TEMPLATE_HEADERS = ['Họ tên', 'Số điện thoại', 'Email', 'Nhãn', 'Giai đoạn', 'Nguồn', 'Ghi chú'] // i18n-ignore

export interface PropertyLite {
  fieldKey: string
  name: string
  fieldType?: string
}

/** Bảng đã đọc từ file: dòng tiêu đề + các dòng dữ liệu (kèm số dòng gốc). */
export interface SheetData {
  headers: string[]
  rows: { rowNumber: number; cells: string[] }[]
}

/** Dòng gửi lên API — khớp `ImportRowInput` ở backend. */
export interface ImportRowInput {
  rowNumber: number
  fullName?: string
  phone?: string
  email?: string
  tags?: string
  lifecycleStage?: string
  source?: string
  notes?: string
  properties?: Record<string, string>
}

// ── Chuỗi ────────────────────────────────────────────────────────────

/** Bỏ dấu, thường hoá, gom khoảng trắng — "Số Điện-Thoại" → "so dien thoai". */
export function foldHeader(s: string): string {
  return s
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/đ/g, 'd').replace(/Đ/g, 'D')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
}

// ── Đoán cột ─────────────────────────────────────────────────────────

const SYNONYMS: Record<BuiltinField, string[]> = {
  fullName: [
    'ho ten', 'ho va ten', 'ten', 'ten khach hang', 'ten khach', 'khach hang', 'ten lien he',
    'name', 'full name', 'fullname', 'customer name', 'contact name', 'ho ten khach hang',
  ],
  phone: [
    'so dien thoai', 'sdt', 'dien thoai', 'dt', 'so dt', 'di dong', 'so di dong', 'phone', 'phone number',
    'mobile', 'tel', 'telephone', 'so zalo', 'zalo', 'sdt khach hang', 'dien thoai di dong',
  ],
  email: ['email', 'e mail', 'mail', 'thu dien tu', 'dia chi email'],
  tags: ['nhan', 'the', 'tag', 'tags', 'label', 'labels', 'the khach hang', 'nhan khach hang'],
  lifecycleStage: ['giai doan', 'trang thai', 'lifecycle', 'stage', 'lifecycle stage', 'vong doi', 'giai doan vong doi'],
  source: ['nguon', 'source', 'kenh', 'nguon khach', 'nguon khach hang', 'kenh tiep can'],
  notes: ['ghi chu', 'note', 'notes', 'mo ta', 'description', 'chu thich'],
}

/** Từ quá chung để khớp kiểu "chứa": "Mã khách hàng", "Trạng thái đơn" không phải tên/giai đoạn. */
const NO_CONTAIN = new Set(['khach hang', 'trang thai', 'ten khach'])

/**
 * Đoán trường cho từng cột theo tiêu đề. Mỗi trường chỉ gán cho MỘT cột (cột đầu
 * tiên khớp). Thứ tự ưu tiên: khớp đúng tên trường sẵn có → khớp đúng tên/khoá thuộc
 * tính CDP → tiêu đề CHỨA một từ đồng nghĩa dài (≥ 5 ký tự, tránh "ten" bắt nhầm
 * "Tên công ty").
 */
export function guessMapping(headers: string[], properties: PropertyLite[] = []): TargetField[] {
  const folded = headers.map(foldHeader)
  const out: TargetField[] = headers.map(() => '')
  const used = new Set<TargetField>()
  const assign = (i: number, t: TargetField) => {
    if (out[i] || used.has(t)) return
    out[i] = t
    used.add(t)
  }

  // 1. Khớp đúng từ đồng nghĩa
  folded.forEach((h, i) => {
    for (const f of BUILTIN_FIELDS) if (SYNONYMS[f.key].includes(h)) { assign(i, f.key); return }
  })
  // 2. Khớp đúng tên / khoá thuộc tính CDP
  folded.forEach((h, i) => {
    const p = properties.find((pp) => foldHeader(pp.name) === h || foldHeader(pp.fieldKey) === h)
    if (p) assign(i, `prop:${p.fieldKey}`)
  })
  // 3. Tiêu đề chứa từ đồng nghĩa dài
  folded.forEach((h, i) => {
    if (out[i] || !h) return
    for (const f of BUILTIN_FIELDS) {
      const hit = SYNONYMS[f.key].some((s) => s.length >= 5 && !NO_CONTAIN.has(s) && ` ${h} `.includes(` ${s} `))
      if (hit) { assign(i, f.key); return }
    }
  })
  return out
}

/** Đã ghép ít nhất một trong tên / SĐT / email chưa. */
export function hasIdentityColumn(mapping: TargetField[]): boolean {
  return mapping.some((m) => m === 'fullName' || m === 'phone' || m === 'email')
}

/** Dựng các dòng gửi API từ bảng + cách ghép cột. Ô trống bị bỏ. */
export function buildImportRows(data: SheetData, mapping: TargetField[]): ImportRowInput[] {
  return data.rows.map(({ rowNumber, cells }) => {
    const r: ImportRowInput = { rowNumber }
    mapping.forEach((target, i) => {
      const v = (cells[i] ?? '').trim()
      if (!target || !v) return
      if (target.startsWith('prop:')) {
        r.properties = { ...(r.properties ?? {}), [target.slice(5)]: v }
      } else {
        const key = target as BuiltinField
        // Hai cột cùng trường (vd. "Nhãn 1", "Nhãn 2") → nối lại.
        r[key] = r[key] ? `${r[key]}${key === 'tags' ? ',' : ' '}${v}` : v
      }
    })
    return r
  })
}

// ── CSV ──────────────────────────────────────────────────────────────

/** Đoán dấu phân cách theo dòng đầu (Excel tiếng Việt hay xuất `;`). */
export function detectDelimiter(text: string): string {
  const firstLine = text.split(/\r?\n/, 1)[0] ?? ''
  let best = ','
  let bestCount = 0
  for (const d of [',', ';', '\t']) {
    let count = 0
    let q = false
    for (const c of firstLine) {
      if (c === '"') q = !q
      else if (!q && c === d) count++
    }
    if (count > bestCount) { best = d; bestCount = count }
  }
  return best
}

/** CSV theo RFC 4180: ngoặc kép, "" thoát, xuống dòng trong ngoặc. */
export function parseCsv(input: string, delimiter?: string): string[][] {
  const text = input.replace(/^﻿/, '')
  const d = delimiter ?? detectDelimiter(text)
  const rows: string[][] = []
  let row: string[] = []
  let field = ''
  let q = false
  for (let i = 0; i < text.length; i++) {
    const c = text[i]
    if (q) {
      if (c === '"') {
        if (text[i + 1] === '"') { field += '"'; i++ } else q = false
      } else field += c
    } else if (c === '"') q = true
    else if (c === d) { row.push(field); field = '' }
    else if (c === '\n' || c === '\r') {
      if (c === '\r' && text[i + 1] === '\n') i++
      row.push(field); rows.push(row); row = []; field = ''
    } else field += c
  }
  if (field !== '' || row.length) { row.push(field); rows.push(row) }
  return rows
}

/** Bảng thô → SheetData: dòng không trống đầu tiên là tiêu đề, bỏ dòng trống. */
export function toSheetData(table: string[][]): SheetData {
  const isBlank = (r: string[]) => r.every((c) => !c || !c.trim())
  const headerIdx = table.findIndex((r) => !isBlank(r))
  if (headerIdx < 0) return { headers: [], rows: [] }
  const headers = table[headerIdx].map((h, i) => h.trim() || i18n.t('custList:import.columnN', { n: i + 1 }))
  const rows: SheetData['rows'] = []
  for (let i = headerIdx + 1; i < table.length; i++) {
    if (isBlank(table[i])) continue
    rows.push({ rowNumber: i + 1, cells: table[i] })
  }
  return { headers, rows }
}

/** Giải mã byte CSV: UTF-8 trước; hỏng thì thử windows-1258 (Excel tiếng Việt cũ). */
export function decodeText(buf: ArrayBuffer): string {
  try {
    return new TextDecoder('utf-8', { fatal: true }).decode(buf)
  } catch {
    try {
      return new TextDecoder('windows-1258').decode(buf)
    } catch {
      return new TextDecoder('utf-8').decode(buf)
    }
  }
}

const csvCell = (v: string) => (/[",\n\r;]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v)

/** Mảng dòng → văn bản CSV có BOM (Excel mở đúng tiếng Việt). */
export function toCsv(rows: string[][]): string {
  return '﻿' + rows.map((r) => r.map((c) => csvCell(c ?? '')).join(',')).join('\r\n')
}

/** File mẫu: cột sẵn có + các thuộc tính CDP của tổ chức. */
export function templateCsv(properties: PropertyLite[] = []): string {
  const headers = [...TEMPLATE_HEADERS, ...properties.map((p) => p.name)]
  const blank = properties.map(() => '')
  return toCsv([
    headers,
    ['Nguyễn Văn An', '0912345678', 'an.nguyen@example.com', 'VIP, Hà Nội', 'Khách hàng', 'Hội chợ', 'Khách quen', ...blank], // i18n-ignore
    ['Trần Thị Bình', '+84 987 654 321', '', 'Sỉ', 'Lead', 'Facebook', '', ...blank], // i18n-ignore
  ])
}

// ── XLSX ─────────────────────────────────────────────────────────────

const XML_ENTITIES: Record<string, string> = { amp: '&', lt: '<', gt: '>', quot: '"', apos: "'" }

export function decodeXml(s: string): string {
  return s.replace(/&(#x[0-9a-fA-F]+|#\d+|amp|lt|gt|quot|apos);/g, (_, e: string) => {
    if (e[0] === '#') return String.fromCodePoint(e[1] === 'x' ? parseInt(e.slice(2), 16) : parseInt(e.slice(1), 10))
    return XML_ENTITIES[e]
  })
}

async function inflateRaw(data: Uint8Array): Promise<Uint8Array> {
  if (typeof DecompressionStream === 'undefined') {
    throw new Error(i18n.t('custList:import.errors.noDecompression'))
  }
  const stream = new Blob([data as BlobPart]).stream().pipeThrough(new DecompressionStream('deflate-raw'))
  return new Uint8Array(await new Response(stream).arrayBuffer())
}

/** Đọc các file cần thiết trong gói ZIP (chỉ phương thức store/deflate). */
export async function unzipEntries(buf: ArrayBuffer, wanted: (name: string) => boolean): Promise<Map<string, string>> {
  const bytes = new Uint8Array(buf)
  const view = new DataView(buf)
  let eocd = -1
  for (let i = bytes.length - 22; i >= Math.max(0, bytes.length - 65_557); i--) {
    if (view.getUint32(i, true) === 0x06054b50) { eocd = i; break }
  }
  if (eocd < 0) throw new Error(i18n.t('custList:import.errors.badXlsx'))
  const count = view.getUint16(eocd + 10, true)
  let p = view.getUint32(eocd + 16, true)
  const out = new Map<string, string>()
  const utf8 = new TextDecoder('utf-8')
  for (let n = 0; n < count; n++) {
    if (view.getUint32(p, true) !== 0x02014b50) break
    const method = view.getUint16(p + 10, true)
    const compSize = view.getUint32(p + 20, true)
    const nameLen = view.getUint16(p + 28, true)
    const extraLen = view.getUint16(p + 30, true)
    const commentLen = view.getUint16(p + 32, true)
    const localOff = view.getUint32(p + 42, true)
    const name = utf8.decode(bytes.subarray(p + 46, p + 46 + nameLen))
    p += 46 + nameLen + extraLen + commentLen
    if (!wanted(name)) continue
    const lNameLen = view.getUint16(localOff + 26, true)
    const lExtraLen = view.getUint16(localOff + 28, true)
    const start = localOff + 30 + lNameLen + lExtraLen
    const raw = bytes.subarray(start, start + compSize)
    if (method === 0) out.set(name, utf8.decode(raw))
    else if (method === 8) out.set(name, utf8.decode(await inflateRaw(raw)))
    else throw new Error(i18n.t('custList:import.errors.badCompression'))
  }
  return out
}

/** "AB12" → 27 (chỉ số cột từ 0). */
export function columnIndex(ref: string): number {
  const letters = ref.match(/^[A-Z]+/i)?.[0].toUpperCase() ?? ''
  let n = 0
  for (const ch of letters) n = n * 26 + (ch.charCodeAt(0) - 64)
  return n - 1
}

const BUILTIN_DATE_FORMATS = new Set([14, 15, 16, 17, 18, 19, 20, 21, 22, 45, 46, 47])

/** Chỉ số style (s=) nào là định dạng ngày — để đổi số serial Excel ra dd/mm/yyyy. */
function dateStyleIndexes(stylesXml: string | undefined): Set<number> {
  const result = new Set<number>()
  if (!stylesXml) return result
  const custom = new Map<number, string>()
  for (const m of stylesXml.matchAll(/<numFmt\b[^>]*numFmtId="(\d+)"[^>]*formatCode="([^"]*)"/g)) {
    custom.set(Number(m[1]), decodeXml(m[2]))
  }
  const cellXfs = stylesXml.match(/<cellXfs\b[^>]*>([\s\S]*?)<\/cellXfs>/)?.[1] ?? ''
  let i = 0
  for (const m of cellXfs.matchAll(/<xf\b([^>]*?)\/?>/g)) {
    const id = Number(m[1].match(/numFmtId="(\d+)"/)?.[1] ?? 0)
    const code = custom.get(id)
    const isDate = BUILTIN_DATE_FORMATS.has(id) ||
      (!!code && /[dy]/i.test(code.replace(/"[^"]*"|\[[^\]]*\]/g, '')))
    if (isDate) result.add(i)
    i++
  }
  return result
}

/** Số serial Excel (hệ 1900) → dd/mm/yyyy. */
export function excelSerialToDate(serial: number): string {
  const ms = Math.round((serial - 25569) * 86_400_000)
  const d = new Date(ms)
  const dd = String(d.getUTCDate()).padStart(2, '0')
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0')
  return `${dd}/${mm}/${d.getUTCFullYear()}`
}

/** Đọc sheet đầu tiên của file .xlsx thành bảng chuỗi. */
export async function parseXlsx(buf: ArrayBuffer): Promise<string[][]> {
  const files = await unzipEntries(buf, (n) =>
    n === 'xl/workbook.xml' || n === 'xl/_rels/workbook.xml.rels' || n === 'xl/sharedStrings.xml' ||
    n === 'xl/styles.xml' || /^xl\/worksheets\/[^/]+\.xml$/.test(n))

  // Sheet đầu tiên theo thứ tự trong workbook (không phải theo tên file).
  let sheetPath = 'xl/worksheets/sheet1.xml'
  const rid = files.get('xl/workbook.xml')?.match(/<sheet\b[^>]*\br:id="([^"]+)"/)?.[1]
  const rels = files.get('xl/_rels/workbook.xml.rels')
  if (rid && rels) {
    const rel = [...rels.matchAll(/<Relationship\b[^>]*>/g)].map((m) => m[0]).find((r) => r.includes(`Id="${rid}"`))
    const target = rel?.match(/Target="([^"]+)"/)?.[1]
    if (target) sheetPath = target.startsWith('/') ? target.slice(1) : `xl/${target.replace(/^\.\//, '')}`
  }
  const sheet = files.get(sheetPath) ?? [...files.entries()].find(([n]) => n.startsWith('xl/worksheets/'))?.[1]
  if (!sheet) throw new Error(i18n.t('custList:import.errors.noSheet'))

  const shared: string[] = []
  for (const m of (files.get('xl/sharedStrings.xml') ?? '').matchAll(/<si\b[^>]*>([\s\S]*?)<\/si>/g)) {
    const body = m[1].replace(/<rPh\b[\s\S]*?<\/rPh>/g, '')
    shared.push([...body.matchAll(/<t\b[^>]*>([\s\S]*?)<\/t>/g)].map((t) => decodeXml(t[1])).join(''))
  }
  const dateStyles = dateStyleIndexes(files.get('xl/styles.xml'))

  const table: string[][] = []
  for (const rm of sheet.matchAll(/<row\b([^>]*?)(?:\/>|>([\s\S]*?)<\/row>)/g)) {
    const rowIdx = Number(rm[1].match(/\br="(\d+)"/)?.[1] ?? table.length + 1) - 1
    const cells: string[] = []
    let next = 0
    for (const cm of (rm[2] ?? '').matchAll(/<c\b([^>]*?)(?:\/>|>([\s\S]*?)<\/c>)/g)) {
      const attrs = cm[1]
      const ref = attrs.match(/\br="([A-Z]+)\d*"/i)?.[1]
      const col = ref ? columnIndex(ref) : next
      next = col + 1
      const type = attrs.match(/\bt="([^"]+)"/)?.[1] ?? 'n'
      const style = Number(attrs.match(/\bs="(\d+)"/)?.[1] ?? -1)
      const inner = cm[2] ?? ''
      const v = inner.match(/<v>([\s\S]*?)<\/v>/)?.[1]
      let value = ''
      if (type === 's') value = v != null ? shared[Number(v)] ?? '' : ''
      else if (type === 'inlineStr') value = [...inner.matchAll(/<t\b[^>]*>([\s\S]*?)<\/t>/g)].map((t) => decodeXml(t[1])).join('')
      else if (type === 'b') value = v === '1' ? 'TRUE' : v === '0' ? 'FALSE' : ''
      else if (v != null) {
        value = decodeXml(v)
        if (type === 'n' && dateStyles.has(style) && value !== '' && Number.isFinite(Number(value))) {
          value = excelSerialToDate(Number(value))
        }
      }
      cells[col] = value
    }
    for (let i = 0; i < cells.length; i++) if (cells[i] == null) cells[i] = ''
    table[rowIdx] = cells
  }
  for (let i = 0; i < table.length; i++) if (!table[i]) table[i] = []
  return table
}

/** Đọc file người dùng chọn (.csv/.txt/.xlsx) → SheetData. */
export async function readSpreadsheetFile(file: File): Promise<SheetData> {
  const name = file.name.toLowerCase()
  const buf = await file.arrayBuffer()
  if (name.endsWith('.xls')) {
    throw new Error(i18n.t('custList:import.errors.xlsUnsupported'))
  }
  const isZip = buf.byteLength > 4 && new DataView(buf).getUint32(0, true) === 0x04034b50
  const table = name.endsWith('.xlsx') || isZip ? await parseXlsx(buf) : parseCsv(decodeText(buf))
  return toSheetData(table)
}

/** Tải một chuỗi CSV xuống máy. */
export function downloadCsv(filename: string, csv: string): void {
  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }))
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}
